import { useEffect, useState } from 'react'

/**
 * Weight is typed in Kg or Ton, and always stored in kg (the one unit in the
 * database), so reports and LRs never mix units. `valueKg` / `onChangeKg` are
 * kg strings; the unit picker only changes how it is typed.
 */
type Unit = 'kg' | 'ton'
const FACTOR: Record<Unit, number> = { kg: 1, ton: 1000 }

function toKg(text: string, unit: Unit): string {
  if (text.trim() === '' || !Number.isFinite(Number(text))) return ''
  return String(Math.round(Number(text) * FACTOR[unit] * 1000) / 1000)
}

/** "2,500 kg (2.5 ton)" for display; kg only when under a ton. */
export function formatWeight(kg: string | number | null | undefined): string {
  if (kg === null || kg === undefined || kg === '') return '—'
  const n = Number(kg)
  if (!Number.isFinite(n)) return '—'
  const fmt = (v: number) => v.toLocaleString('en-IN', { maximumFractionDigits: 3 })
  return n >= 1000 ? `${fmt(n)} kg (${fmt(n / 1000)} ton)` : `${fmt(n)} kg`
}

export function WeightInput({
  label,
  valueKg,
  onChangeKg,
  required = false,
}: {
  label: string
  valueKg: string
  onChangeKg: (kg: string) => void
  required?: boolean
}) {
  const [unit, setUnit] = useState<Unit>('kg')
  const [text, setText] = useState(valueKg === '' ? '' : String(Number(valueKg)))

  // The parent can change the value too (form cleared, LR prefilled): follow it,
  // but never rewrite what is being typed.
  useEffect(() => {
    if (valueKg !== toKg(text, unit)) setText(valueKg === '' ? '' : String(Number(valueKg) / FACTOR[unit]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueKg])

  return (
    <div className="tf-field">
      <span>{label}</span>
      <div className="tf-weight-row">
        <input
          type="number"
          min="0"
          step="any"
          aria-label={label}
          value={text}
          required={required}
          onChange={(e) => {
            setText(e.target.value)
            onChangeKg(toKg(e.target.value, unit))
          }}
        />
        <select
          aria-label={`${label} unit`}
          value={unit}
          onChange={(e) => {
            const next = e.target.value as Unit
            setUnit(next)
            onChangeKg(toKg(text, next)) // same number, new unit
          }}
        >
          <option value="kg">Kg</option>
          <option value="ton">Ton</option>
        </select>
      </div>
    </div>
  )
}
